import { di } from 'react-magnetic-di';

import { fg } from '@atlaskit/platform-feature-flags';
import { type Agent } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { performGqlFetch } from '@atlassian/conversation-assistant-utils/graphqlUtils';

import { transformAgentGqlResponse } from './utils.tsx';

export type PageInfo = {
	hasNextPage: boolean;
	hasPreviousPage: boolean;
	// null is a valid value from graphQL response. It means a fetch has been made and there are no results.
	// undefined is an initial value we sent. It means a fetch has not been made yet (and thus, there are no results).
	startCursor: string | null | undefined;
	endCursor: string | null | undefined;
};

export const DEFAULT_PAGE_INFO: PageInfo = {
	hasNextPage: false,
	hasPreviousPage: false,
	startCursor: undefined,
	endCursor: undefined,
};

export type AgentsAggResponse = {
	agents: Agent[];
	pageInfo: PageInfo;
};

const transformAgentListToGqlResponse = (response: any): AgentsAggResponse => {
	try {
		// data can be null if the `abortController` is aborted
		const { edges, pageInfo } = response?.data?.agentStudio_getAgents ?? {
			pageInfo: DEFAULT_PAGE_INFO,
		};

		const agents =
			edges
				?.map((edge: any) => transformAgentGqlResponse(edge.node))
				.filter((agent: Agent) => !!agent.id) ?? [];

		return { agents, pageInfo };
	} catch {
		return {
			agents: [],
			pageInfo: {
				hasNextPage: false,
				hasPreviousPage: false,
				startCursor: undefined,
				endCursor: undefined,
			},
		};
	}
};

type AggParams = {
	cloudId: string;
	// Number of agents to fetch
	count?: number;
	// Cursor token for pagination, e.g "from this point"
	after?: string;
	// Product calling this hook, defaults to Rovo
	product?: string;
	// Workspace ID to fetch agents for, required if product is workspace-scoped (e.g. Bitbucket, Trello)
	workspaceId?: string | null;
	// Filtering
	input?: {
		onlyFavouriteAgents?: boolean;
		onlyMyAgents?: boolean;
		// This will search agent name, and also description on the BE
		name?: string;
	};
	abortController?: AbortController;
};

const agentStudioAssistantFragment = `
fragment conversationAssistantAgent_agentStudioAssistant on AgentStudioAssistant {
  id
  name
  description
  conversationStarters
  isFavourite
  creatorType
  identityAccountId
  externalConfigReference
  creator {
    id
  }
  icon
  authoringTeam @skip(if: $skipAuthoringTeam) {
    id
    profileUrl
    displayName
		smallAvatarImageUrl
  }
  isVerified
}
`;

export const fetchPaginatedAgents = async ({
	cloudId,
	count = 10,
	after,
	product,
	input,
	abortController,
	workspaceId,
}: AggParams): Promise<AgentsAggResponse> => {
	di(performGqlFetch);

	const gqlQuery = `
	query agentStudioGetAgents($cloudId: String!, $first: Int, $input: AgentStudioAgentQueryInput, $after: String, $product: String, $workspaceId: String, $skipAuthoringTeam: Boolean = false) {
		agentStudio_getAgents(
			cloudId: $cloudId
			first: $first
			input: $input
			after: $after
			product: $product
			workspaceId: $workspaceId
		) @optIn(to: "AgentStudio") {
			edges {
				node {
					...conversationAssistantAgent_agentStudioAssistant
				}
			}
			pageInfo {
				startCursor
				endCursor
				hasNextPage
				hasPreviousPage
			}
		}
	}

	${agentStudioAssistantFragment}
	`;

	const variables = {
		cloudId,
		first: count,
		after,
		product,
		input,
		workspaceId,
		skipAuthoringTeam: fg('rovo_agents_fetch_agents_without_authoring_team'),
	};

	const response = await performGqlFetch({
		gqlQuery,
		variables,
		abortController,
	});

	return transformAgentListToGqlResponse(response);
};

type GetByExternalReferenceResponse = {
	agentStudio_getByExternalReference: {
		conversationStarters: string[];
		name: string;
		description: string;
		creatorType: string;
		identityAccountId: string;
		externalConfigReference: string;
		creator: {
			id: string;
		};
	};
};

export const fetchAgentByExternalConfigReference = async ({
	cloudId,
	externalConfigReference,
}: {
	cloudId: string;
	externalConfigReference: string;
}): Promise<Agent> => {
	di(performGqlFetch);

	const gqlQuery = `
	query agentStudioGetAgentByExternalConfigReference($cloudId: String!, $externalConfigReference: String!, $skipAuthoringTeam: Boolean = false) {
		agentStudio_getByExternalReference(cloudId: $cloudId, externalReference: $externalConfigReference) @optIn(to: "AgentStudio") {
			...conversationAssistantAgent_agentStudioAssistant
			}
		}

		${agentStudioAssistantFragment}
		`;

	const variables = {
		cloudId,
		externalConfigReference,
		skipAuthoringTeam: fg('rovo_agents_fetch_agents_without_authoring_team'),
	};

	const response = await performGqlFetch<
		GetByExternalReferenceResponse,
		{ cloudId: string; externalConfigReference: string; skipAuthoringTeam: boolean }
	>({
		gqlQuery,
		variables,
	});

	return transformAgentGqlResponse(response.data.agentStudio_getByExternalReference);
};
